import type {
  Page,
  PageListResponse,
  PageRequest,
  PageCreatePayload,
  PageUpdatePayload,
  PageErrorResponse,
} from '../Types/Pages.types';
import { authenticatedFetch } from '@/Auth/services/auth.service';
import { env } from '@/config/env';

const PAGES_ENDPOINT = '/v1/pages';

export const getPages = async (params: PageRequest = {}): Promise<PageListResponse> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params.tenantSlug) queryParams.append('tenantSlug', params.tenantSlug);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.isMain !== undefined) queryParams.append('isMain', params.isMain.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const url = `${env.bffBaseURL}${PAGES_ENDPOINT}${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData: PageErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch pages',
        statusCode: response.status,
      }));
      if (response.status === 403) throw new Error('You do not have permission to view pages');
      throw new Error(errorData.message || 'Failed to fetch pages');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred');
  }
};

export const getPageById = async (id: string): Promise<Page> => {
  try {
    const url = `${env.bffBaseURL}${PAGES_ENDPOINT}/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData: PageErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch page',
        statusCode: response.status,
      }));
      if (response.status === 403) throw new Error('You do not have permission to view this page');
      if (response.status === 404) throw new Error('Page not found');
      throw new Error(errorData.message || 'Failed to fetch page');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred');
  }
};

export const createPage = async (payload: PageCreatePayload): Promise<Page> => {
  try {
    const url = `${env.bffBaseURL}${PAGES_ENDPOINT}`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: PageErrorResponse = await response.json().catch(() => ({
        message: 'Failed to create page',
        statusCode: response.status,
      }));
      if (response.status === 403) throw new Error('You do not have permission to create pages');
      if (response.status === 409)
        throw new Error('A page with this slug already exists for this tenant');
      throw new Error(errorData.message || 'Failed to create page');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred while creating page');
  }
};

export const updatePage = async (id: string, payload: PageUpdatePayload): Promise<Page> => {
  try {
    const url = `${env.bffBaseURL}${PAGES_ENDPOINT}/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData: PageErrorResponse = await response.json().catch(() => ({
        message: 'Failed to update page',
        statusCode: response.status,
      }));
      if (response.status === 403)
        throw new Error('You do not have permission to update this page');
      if (response.status === 404) throw new Error('Page not found');
      if (response.status === 409)
        throw new Error('A page with this slug already exists for this tenant');
      throw new Error(errorData.message || 'Failed to update page');
    }

    return response.json();
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred while updating page');
  }
};

export const deletePage = async (id: string): Promise<void> => {
  try {
    const url = `${env.bffBaseURL}${PAGES_ENDPOINT}/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorData: PageErrorResponse = await response.json().catch(() => ({
        message: 'Failed to delete page',
        statusCode: response.status,
      }));
      if (response.status === 403)
        throw new Error('You do not have permission to delete this page');
      if (response.status === 404) throw new Error('Page not found');
      throw new Error(errorData.message || 'Failed to delete page');
    }
  } catch (error: unknown) {
    if (error instanceof Error) throw error;
    throw new Error('An unexpected error occurred while deleting page');
  }
};
